import { useState, useEffect } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { APP_CONFIG } from '../config/appConfig';

const RETRY_DELAY = 2000; // 2 secondes
const MAX_RETRIES = 3;

// Singleton pour éviter le double chargement de l'API
let loaderPromise: Promise<typeof google> | null = null;
let isGoogleMapsLoaded = false;

export const useGoogleMapsLoader = () => {
  const [isLoaded, setIsLoaded] = useState(isGoogleMapsLoaded);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let retryCount = 0;
    let timeoutId: NodeJS.Timeout;

    const loadMapsApi = async () => {
      try {
        const apiKey = APP_CONFIG.maps.googleMapsApiKey;

        // Places API will be initialized by the loader

        if (!apiKey) {
          throw new Error('Google Maps API key is missing. Please check your .env file for PUBLIC_GOOGLE_MAPS_API_KEY or VITE_GOOGLE_MAPS_API_KEY');
        }

        // Utiliser le singleton pour éviter de charger l'API plusieurs fois
        if (!loaderPromise) {
          const loader = new Loader({
            apiKey,
            version: 'weekly',
            libraries: ['places'],
            language: 'fr',
            region: 'FR'
          });
          loaderPromise = loader.load();
        }

        await loaderPromise;
        isGoogleMapsLoaded = true;
        setIsLoaded(true);
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load Google Maps API';
        setError(errorMessage);

        if (retryCount < MAX_RETRIES) {
          retryCount++;
          timeoutId = setTimeout(loadMapsApi, RETRY_DELAY);
        }
      }
    };

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      isGoogleMapsLoaded = true;
      setIsLoaded(true);
      setError(null);
    } else {
      loadMapsApi();
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return { isLoaded, error };
};
